import clsx from 'clsx';
import { useId, type ReactNode } from 'react';
import { FieldContext } from './field-context';
import styles from './Field.module.css';

export interface FieldProps {
  label: ReactNode;
  /** Help text under the control. */
  help?: ReactNode;
  /** Error message; marks the control invalid. */
  error?: ReactNode;
  required?: boolean;
  /** Render as `<fieldset>` + `<legend>` (checkbox/radio groups) instead of `<label>`. */
  group?: boolean;
  /** Put the label next to the control (switches/checkbox rows). */
  inline?: boolean;
  /** Explicit control id (defaults to a generated one). */
  id?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Label + control + help + error. Controls from this library (`Input`, `NumberInput`,
 * `Select`, `Textarea`, `Switch`, …) pick up the id/aria wiring automatically.
 */
export function Field({
  label,
  help,
  error,
  required = false,
  group = false,
  inline = false,
  id,
  className,
  children,
}: FieldProps) {
  const generated = useId();
  const controlId = id ?? generated;
  const helpId = help ? `${controlId}-help` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;
  const context = { id: controlId, describedBy, invalid: Boolean(error), required };

  const labelContent = (
    <>
      {label}
      {required && (
        <span className={styles.required} aria-hidden>
          {' '}
          *
        </span>
      )}
    </>
  );
  const messages = (
    <>
      {help && (
        <p id={helpId} className={styles.help}>
          {help}
        </p>
      )}
      {error && (
        <p id={errorId} className={styles.error}>
          {error}
        </p>
      )}
    </>
  );

  if (group) {
    return (
      <fieldset className={clsx(styles.field, styles.group, className)} aria-describedby={describedBy}>
        <legend className={styles.label}>{labelContent}</legend>
        <FieldContext value={context}>{children}</FieldContext>
        {messages}
      </fieldset>
    );
  }
  return (
    <div className={clsx(styles.field, inline && styles.inline, className)}>
      <label htmlFor={controlId} className={styles.label}>
        {labelContent}
      </label>
      <FieldContext value={context}>
        {inline ? <div className={styles.inlineControl}>{children}</div> : children}
      </FieldContext>
      {messages}
    </div>
  );
}
